import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import apiService from '../services/api';
import toast from 'react-hot-toast';

interface ThemeContextType {
  accentColor: string;
  setAccentColor: (color: string) => void;
  updateAccentColor: (color: string) => Promise<void>;
  isDark: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [accentColor, setAccentColor] = useState('#3B82F6'); // Default blue
  const [isDark, setIsDark] = useState<boolean>(() => {
    // Initialize from global localStorage or system preference
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme');
      if (stored === 'dark') return true;
      if (stored === 'light') return false;
      // Fall back to system preference
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  // Update CSS custom properties
  const updateCSSVariables = (color: string) => {
    const root = document.documentElement;
    root.style.setProperty('--accent-color', color);
    root.style.setProperty('--accent-color-50', `${color}0D`);
    root.style.setProperty('--accent-color-100', `${color}1A`);
    root.style.setProperty('--accent-color-500', color);
    root.style.setProperty('--accent-color-600', darkenColor(color, 10));
    root.style.setProperty('--accent-color-700', darkenColor(color, 20));
    root.style.setProperty('--accent-color-900', darkenColor(color, 40));
  };

  // Hydrate from localStorage immediately to avoid flicker
  useEffect(() => {
    const storedAccent = typeof window !== 'undefined' ? localStorage.getItem('companyAccentColor') : null;
    if (storedAccent) {
      setAccentColor(storedAccent);
      updateCSSVariables(storedAccent);
    } else {
      updateCSSVariables(accentColor);
    }

    // Apply initial theme - force light on landing page
    const currentIsLandingPage = typeof window !== 'undefined' && window.location.pathname === '/';
    const effectiveIsDark = currentIsLandingPage ? false : isDark;
    if (effectiveIsDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load company accent color when user/company changes
  useEffect(() => {
    const loadCompanyTheme = async () => {
      if (user?.companyId) {
        try {
          const response = await apiService.getCompanyById(user.companyId);
          const color = response.success && response.data?.accentColor ? response.data.accentColor : null;
          if (color) {
            setAccentColor(color);
            updateCSSVariables(color);
            try { localStorage.setItem('companyAccentColor', color); } catch {}
            // Persist logo too for header fallback
            if (response.data?.logoUrl) {
              try { localStorage.setItem('companyLogoUrl', response.data.logoUrl); } catch {}
            }
          }
        } catch (error) {
          console.error('Error loading company theme:', error);
        }
      }
    };

    loadCompanyTheme();
  }, [user?.companyId]);

  // Helper to darken a hex color
  const darkenColor = (hex: string, percent: number): string => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = (num >> 8 & 0x00FF) - amt;
    const B = (num & 0x0000FF) - amt;
    return '#' + (0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)).toString(16).slice(1);
  };

  // When the authenticated user changes, load any user-specific theme preference
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // Prefer per-user theme; fall back to global theme if none is stored
    const userThemeKey = user?.id ? `theme:${user.id}` : null;
    const storedUserTheme = userThemeKey ? localStorage.getItem(userThemeKey) : null;
    const storedGlobalTheme = localStorage.getItem('theme');

    if (storedUserTheme === 'dark' || storedUserTheme === 'light') {
      setIsDark(storedUserTheme === 'dark');
      return;
    }

    if (storedGlobalTheme === 'dark' || storedGlobalTheme === 'light') {
      setIsDark(storedGlobalTheme === 'dark');
    }
  }, [user?.id]);

  const updateAccentColor = async (color: string) => {
    try {
      const currentCompanyResponse = await apiService.getCompany();
      if (!currentCompanyResponse.success || !currentCompanyResponse.data) {
        throw new Error('Could not get current company data');
      }

      const response = await apiService.updateCompany({
        name: currentCompanyResponse.data.name,
        description: currentCompanyResponse.data.description,
        address: currentCompanyResponse.data.address,
        city: currentCompanyResponse.data.city,
        state: currentCompanyResponse.data.state,
        zipCode: currentCompanyResponse.data.zipCode,
        phone: currentCompanyResponse.data.phone,
        email: currentCompanyResponse.data.email,
        website: currentCompanyResponse.data.website,
        timeZone: currentCompanyResponse.data.timeZone,
        currencyCode: currentCompanyResponse.data.currencyCode,
        sessionExpiryMinutes: currentCompanyResponse.data.sessionExpiryMinutes,
        sessionWarningLeadSeconds: currentCompanyResponse.data.sessionWarningLeadSeconds,
        accentColor: color
      });
      
      if (response.success) {
        setAccentColor(color);
        updateCSSVariables(color);
        try { localStorage.setItem('companyAccentColor', color); } catch {}
        toast.success('Accent color updated successfully!');
      } else {
        toast.error('Failed to update accent color');
      }
    } catch (error) {
      console.error('Error updating accent color:', error);
      toast.error('Failed to update accent color');
      throw error;
    }
  };

  // Keep CSS in sync if state changes elsewhere
  useEffect(() => {
    updateCSSVariables(accentColor);
  }, [accentColor]);

  // Update DOM when theme changes or route changes
  useEffect(() => {
    // Check current route to determine if we're on landing page
    const currentIsLandingPage = typeof window !== 'undefined' && window.location.pathname === '/';
    
    // Force light theme on landing page
    const effectiveIsDark = currentIsLandingPage ? false : isDark;
    const themeValue = effectiveIsDark ? 'dark' : 'light';

    if (effectiveIsDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Always keep a global theme for unauthenticated screens
    try {
      localStorage.setItem('theme', themeValue);
    } catch {}

    // Additionally store a per-user theme when we have a user
    // Only save if not on landing page (to preserve user preference)
    if (user?.id && !currentIsLandingPage) {
      try {
        localStorage.setItem(`theme:${user.id}`, themeValue);
      } catch {}
    }
  }, [isDark, user?.id]);

  // Listen for route changes to update theme when navigating to/from landing page
  useEffect(() => {
    const handleRouteChange = () => {
      const currentIsLandingPage = typeof window !== 'undefined' && window.location.pathname === '/';
      const effectiveIsDark = currentIsLandingPage ? false : isDark;
      
      if (effectiveIsDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    };

    // Check on mount and listen for navigation events
    handleRouteChange();
    
    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', handleRouteChange);
    
    // Use a small interval to check for route changes (React Router doesn't trigger popstate)
    // Check every 300ms - frequent enough to feel responsive, not too frequent to impact performance
    const intervalId = setInterval(handleRouteChange, 300);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      clearInterval(intervalId);
    };
  }, [isDark]);

  // Toggle theme function
  const toggleTheme = () => {
    setIsDark(prev => !prev);
  };

  const value: ThemeContextType = {
    accentColor,
    setAccentColor,
    updateAccentColor,
    isDark,
    toggleTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
